import { useState, useCallback } from 'react';

import { contactsApi } from '@lib/apiClients';
import { microServicesRoutes } from '@constants/microServicesRoutes';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';

import type {
  CallSessionConfig,
  CallSessionConfigResponse,
  CreateCallSessionConfigDto,
  UpdateCallSessionConfigDto,
  OngoingCallAction,
} from '../types/wizard.types';

interface UseCallSessionConfigApi {
  createCallSessionConfig: (
    sessionId: string,
    action: OngoingCallAction
  ) => Promise<CallSessionConfigResponse>;
  getCallSessionConfig: (sessionId: string) => Promise<CallSessionConfigResponse>;
  updateCallSessionConfig: (
    sessionId: string,
    action: OngoingCallAction
  ) => Promise<CallSessionConfigResponse>;
  isLoading: boolean;
}

export default function useCallSessionConfigApi (): UseCallSessionConfigApi {
  const [isLoading, setIsLoading] = useState(false);

  const createCallSessionConfig = useCallback(async (
    sessionId: string,
    action: OngoingCallAction,
  ): Promise<CallSessionConfigResponse> => {
    setIsLoading(true);

    try {
      const payload: CreateCallSessionConfigDto = {
        callSessionId: sessionId,
        ongoingCallAction: action,
      };

      const url = microServicesRoutes.contacts.post.callSessionConfig.create;
      const response = await contactsApi.post<CallSessionConfig>(url, payload);

      await logInfo(
        'Created call session config successfully',
        'Wizard/useCallSessionConfigApi/createCallSessionConfig',
        { sessionId, action },
        false,
      );

      setIsLoading(false);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      await logError(
        'Failed to create call session config',
        'Wizard/useCallSessionConfigApi/createCallSessionConfig',
        { error, sessionId },
        false,
      );

      setIsLoading(false);

      const errorMessage = error instanceof Error ? error.message : 'Failed to create config';
      return {
        success: false,
        error: { message: errorMessage },
      };
    }
  }, []);

  const getCallSessionConfig = useCallback(async (
    sessionId: string,
  ): Promise<CallSessionConfigResponse> => {
    setIsLoading(true);

    try {
      const url = `${microServicesRoutes.contacts.get.callSessionConfig.getByCallSessionId}/${sessionId}`;
      const response = await contactsApi.get<CallSessionConfig>(url);

      setIsLoading(false);

      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };

      // 404 means no config exists, which is valid (default to CUT_OFF)
      if (axiosError.response?.status === 404) {
        setIsLoading(false);
        return {
          success: true,
          data: undefined,
        };
      }

      await logError(
        'Failed to get call session config',
        'Wizard/useCallSessionConfigApi/getCallSessionConfig',
        { error, sessionId },
        false,
      );

      setIsLoading(false);

      const errorMessage = error instanceof Error ? error.message : 'Failed to get config';
      return {
        success: false,
        error: { message: errorMessage },
      };
    }
  }, []);

  const updateCallSessionConfig = useCallback(async (
    sessionId: string,
    action: OngoingCallAction,
  ): Promise<CallSessionConfigResponse> => {
    setIsLoading(true);

    try {
      const payload: UpdateCallSessionConfigDto = {
        ongoingCallAction: action,
      };

      const url = `${microServicesRoutes.contacts.put.callSessionConfig.updateByCallSessionId}/${sessionId}`;
      const response = await contactsApi.put<CallSessionConfig>(url, payload);

      await logInfo(
        'Updated call session config successfully',
        'Wizard/useCallSessionConfigApi/updateCallSessionConfig',
        { sessionId, action },
        false,
      );

      setIsLoading(false);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      await logError(
        'Failed to update call session config',
        'Wizard/useCallSessionConfigApi/updateCallSessionConfig',
        { error, sessionId },
        false,
      );

      setIsLoading(false);

      const errorMessage = error instanceof Error ? error.message : 'Failed to update config';
      return {
        success: false,
        error: { message: errorMessage },
      };
    }
  }, []);

  return {
    createCallSessionConfig,
    getCallSessionConfig,
    updateCallSessionConfig,
    isLoading,
  };
}
